import axios from "axios";
import React from "react";
import {
  MdHistory,
  MdLockOutline,
  MdSearch,
  MdStar,
} from "react-icons/md";
import { getRequestHeaders } from "../api";
import { baseUrl } from "../assets/Animations";
import { getTeacherImageUrl } from "../AtharoHub/teacherProfileUtils";
import "./teacher-evaluations.css";

const METRICS = [
  {
    key: "responsiveness",
    label: "Responsiveness",
    helper: "How promptly and consistently they respond to families and the team.",
  },
  {
    key: "studentRapport",
    label: "Student Rapport",
    helper: "How well they connect with, motivate, and retain students.",
  },
  {
    key: "reliability",
    label: "Reliability",
    helper: "How dependably they arrive prepared and follow through.",
  },
  {
    key: "ownership",
    label: "Ownership",
    helper: "How proactively they solve problems and own outcomes.",
  },
  {
    key: "communication",
    label: "Communication",
    helper: "How clearly and thoughtfully they communicate with others.",
  },
];

const SCHOOL_NAMES = {
  mma: "Mountainside Music Academy",
  lsma: "Lakeside Music Academy",
  lma: "Lakeside Music Academy",
  tma: "Treeside Music Academy",
  tsma: "Treeside Music Academy",
  mor: "Morningside Music Academy",
  morma: "Morningside Music Academy",
  psma: "Parkside Music Academy",
};

const emptyScores = () =>
  METRICS.reduce((result, metric) => ({ ...result, [metric.key]: null }), {});

const emptyNotes = () =>
  METRICS.reduce((result, metric) => ({ ...result, [metric.key]: "" }), {});

const displaySchool = (value) => {
  const school = String(value || "").trim();
  return SCHOOL_NAMES[school.toLowerCase()] || school || "Atharo Music";
};

const readableDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const evaluationAverage = (evaluation) => {
  const scores = METRICS.map((metric) =>
    Number(evaluation?.metrics?.[metric.key]?.score)
  ).filter(Number.isFinite);
  if (!scores.length) return null;
  return scores.reduce((total, score) => total + score, 0) / scores.length;
};

const apiError = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const TeacherAvatar = ({ teacher, size = "regular" }) => {
  const [failed, setFailed] = React.useState(false);
  const imageUrl = getTeacherImageUrl(teacher);
  const initials = String(teacher?.Name || teacher?.teacherName || "T")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  React.useEffect(() => setFailed(false), [imageUrl]);

  return (
    <span className={`teacher-evaluations__avatar is-${size}`} aria-hidden="true">
      {imageUrl && !failed ? (
        <img src={imageUrl} alt="" onError={() => setFailed(true)} />
      ) : (
        <span>{initials}</span>
      )}
    </span>
  );
};

const TeacherEvaluations = ({ userData }) => {
  const [teachers, setTeachers] = React.useState([]);
  const [evaluations, setEvaluations] = React.useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [selectedSchool, setSelectedSchool] = React.useState("all");
  const [scores, setScores] = React.useState(emptyScores);
  const [notes, setNotes] = React.useState(emptyNotes);
  const [overallSummary, setOverallSummary] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState(null);

  React.useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setMessage(null);
      try {
        const [teacherResponse, evaluationResponse] = await Promise.all([
          axios.get(`${baseUrl}/teacher-database-active`, getRequestHeaders()),
          axios.get(`${baseUrl}/teacher-evaluations`, getRequestHeaders()),
        ]);
        if (!active) return;

        const activeTeachers = (Array.isArray(teacherResponse.data)
          ? teacherResponse.data
          : []
        )
          .filter(
            (teacher) =>
              teacher?.TeacherID &&
              String(teacher?.Status || "").trim().toLowerCase() !== "left school" &&
              String(teacher?.School || "").trim().toLowerCase() !== "admin"
          )
          .sort((a, b) =>
            String(a.Name || "").localeCompare(String(b.Name || ""))
          );
        setTeachers(activeTeachers);
        setEvaluations(evaluationResponse.data?.evaluations || []);
        setSelectedTeacherId((current) => current || activeTeachers[0]?.TeacherID || "");
      } catch (error) {
        if (active) {
          setMessage({
            type: "error",
            text: apiError(error, "Unable to load teacher evaluations."),
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (userData?.IsAdmin === true) load();
    return () => {
      active = false;
    };
  }, [userData?.IsAdmin]);

  const evaluationsByTeacher = React.useMemo(() => {
    const result = new Map();
    evaluations.forEach((evaluation) => {
      const current = result.get(evaluation.teacherId) || [];
      current.push(evaluation);
      result.set(evaluation.teacherId, current);
    });
    return result;
  }, [evaluations]);

  const filteredTeachers = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return teachers.filter((teacher) => {
      const teacherSchool = String(teacher.School || "").trim().toLowerCase();
      const schoolMatches =
        selectedSchool === "all" || teacherSchool === selectedSchool;
      const searchMatches =
        !query ||
        [teacher.Name, displaySchool(teacher.School)]
          .map((value) => String(value || "").toLowerCase())
          .some((value) => value.includes(query));
      return schoolMatches && searchMatches;
    });
  }, [search, selectedSchool, teachers]);

  const schoolOptions = React.useMemo(() => {
    const schools = new Map();
    teachers.forEach((teacher) => {
      const value = String(teacher.School || "").trim().toLowerCase();
      if (value && !schools.has(value)) {
        schools.set(value, displaySchool(teacher.School));
      }
    });
    return Array.from(schools, ([value, label]) => ({ value, label })).sort(
      (a, b) => a.label.localeCompare(b.label)
    );
  }, [teachers]);

  React.useEffect(() => {
    if (loading) return;
    const selectedIsVisible = filteredTeachers.some(
      (teacher) => teacher.TeacherID === selectedTeacherId
    );
    if (!selectedIsVisible) {
      setSelectedTeacherId(filteredTeachers[0]?.TeacherID || "");
      setScores(emptyScores());
      setNotes(emptyNotes());
      setOverallSummary("");
      setMessage(null);
    }
  }, [filteredTeachers, loading, selectedTeacherId]);

  const selectedTeacher = teachers.find(
    (teacher) => teacher.TeacherID === selectedTeacherId
  );
  const teacherHistory = evaluationsByTeacher.get(selectedTeacherId) || [];
  const complete = METRICS.every((metric) => scores[metric.key] !== null);

  const resetForm = () => {
    setScores(emptyScores());
    setNotes(emptyNotes());
    setOverallSummary("");
  };

  const selectTeacher = (teacherId) => {
    setSelectedTeacherId(teacherId);
    resetForm();
    setMessage(null);
  };

  const saveEvaluation = async (event) => {
    event.preventDefault();
    if (!selectedTeacher || !complete || saving) return;

    setSaving(true);
    setMessage(null);
    try {
      const metrics = METRICS.reduce(
        (result, metric) => ({
          ...result,
          [metric.key]: {
            score: scores[metric.key],
            note: notes[metric.key],
          },
        }),
        {}
      );
      const response = await axios.post(
        `${baseUrl}/teacher-evaluations`,
        {
          teacherId: selectedTeacher.TeacherID,
          metrics,
          overallSummary,
        },
        getRequestHeaders()
      );
      setEvaluations((current) => [response.data.evaluation, ...current]);
      resetForm();
      setMessage({ type: "success", text: "Evaluation saved to the private history." });
    } catch (error) {
      setMessage({
        type: "error",
        text: apiError(error, "Unable to save this evaluation."),
      });
    } finally {
      setSaving(false);
    }
  };

  if (userData?.IsAdmin !== true) return null;

  return (
    <main className="teacher-evaluations">
      <header className="teacher-evaluations__header">
        <div>
          <span className="teacher-evaluations__eyebrow">TEACHER DEVELOPMENT</span>
          <h1>Teacher Evaluations</h1>
          <p>Record thoughtful feedback and review each teacher’s history over time.</p>
        </div>
        <div className="teacher-evaluations__privacy">
          <MdLockOutline />
          Private to admins
        </div>
      </header>

      {message && (
        <div
          className={`teacher-evaluations__message is-${message.type}`}
          role={message.type === "error" ? "alert" : "status"}
        >
          {message.text}
        </div>
      )}

      <section className="teacher-evaluations__workspace">
        <aside className="teacher-evaluations__roster">
          <div className="teacher-evaluations__roster-heading">
            <div>
              <h2>Active teachers</h2>
              <span>{teachers.length}</span>
            </div>
            <label className="teacher-evaluations__search">
              <MdSearch />
              <span className="sr-only">Search teachers</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search teachers"
              />
            </label>
            <label className="teacher-evaluations__school-filter">
              <span>School</span>
              <select
                aria-label="Filter teachers by school"
                value={selectedSchool}
                onChange={(event) => setSelectedSchool(event.target.value)}
              >
                <option value="all">All schools</option>
                {schoolOptions.map((school) => (
                  <option key={school.value} value={school.value}>
                    {school.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="teacher-evaluations__teacher-list">
            {loading ? (
              <p className="teacher-evaluations__empty">Loading teachers…</p>
            ) : filteredTeachers.length ? (
              filteredTeachers.map((teacher) => {
                const latest = evaluationsByTeacher.get(teacher.TeacherID)?.[0];
                const average = evaluationAverage(latest);
                return (
                  <button
                    key={teacher.TeacherID}
                    type="button"
                    className={`teacher-evaluations__teacher-button${
                      teacher.TeacherID === selectedTeacherId ? " is-selected" : ""
                    }`}
                    onClick={() => selectTeacher(teacher.TeacherID)}
                  >
                    <TeacherAvatar teacher={teacher} />
                    <span className="teacher-evaluations__teacher-copy">
                      <strong>{teacher.Name || "Unnamed teacher"}</strong>
                      <small>{displaySchool(teacher.School)}</small>
                    </span>
                    {average !== null ? (
                      <span className="teacher-evaluations__latest-score">
                        <MdStar /> {average.toFixed(1)}
                      </span>
                    ) : (
                      <span className="teacher-evaluations__not-rated">New</span>
                    )}
                  </button>
                );
              })
            ) : (
              <p className="teacher-evaluations__empty">No active teachers match that search.</p>
            )}
          </div>
        </aside>

        <div className="teacher-evaluations__content">
          {selectedTeacher ? (
            <>
              <div className="teacher-evaluations__profile">
                <TeacherAvatar teacher={selectedTeacher} size="large" />
                <div>
                  <span>{displaySchool(selectedTeacher.School)}</span>
                  <h2>{selectedTeacher.Name}</h2>
                  <p>
                    {teacherHistory.length
                      ? `${teacherHistory.length} historical evaluation${
                          teacherHistory.length === 1 ? "" : "s"
                        }`
                      : "No evaluations yet"}
                  </p>
                </div>
              </div>

              <form className="teacher-evaluations__form" onSubmit={saveEvaluation}>
                <div className="teacher-evaluations__section-heading">
                  <div>
                    <span>NEW EVALUATION</span>
                    <h3>Rate each characteristic</h3>
                  </div>
                  <p>1 needs improvement · 5 is excellent</p>
                </div>

                <div className="teacher-evaluations__metric-list">
                  {METRICS.map((metric) => (
                    <article className="teacher-evaluations__metric" key={metric.key}>
                      <div className="teacher-evaluations__metric-heading">
                        <div>
                          <h4>{metric.label}</h4>
                          <p>{metric.helper}</p>
                        </div>
                        <div
                          className="teacher-evaluations__score-picker"
                          aria-label={`${metric.label} score`}
                        >
                          {[1, 2, 3, 4, 5].map((score) => (
                            <button
                              key={score}
                              type="button"
                              aria-label={`${metric.label} score ${score}`}
                              aria-pressed={scores[metric.key] === score}
                              className={scores[metric.key] === score ? "is-selected" : ""}
                              onClick={() =>
                                setScores((current) => ({
                                  ...current,
                                  [metric.key]: score,
                                }))
                              }
                            >
                              {score}
                            </button>
                          ))}
                        </div>
                      </div>
                      <label>
                        <span className="sr-only">{metric.label} notes</span>
                        <textarea
                          aria-label={`${metric.label} notes`}
                          value={notes[metric.key]}
                          maxLength={600}
                          onChange={(event) => {
                            const value = event.target.value;
                            setNotes((current) => ({
                              ...current,
                              [metric.key]: value,
                            }));
                          }}
                          placeholder={`Add a short note about ${metric.label.toLowerCase()}…`}
                        />
                      </label>
                    </article>
                  ))}
                </div>

                <label className="teacher-evaluations__summary-field">
                  <span>Overall summary <em>Optional</em></span>
                  <textarea
                    value={overallSummary}
                    maxLength={2000}
                    onChange={(event) => setOverallSummary(event.target.value)}
                    placeholder="Add any context, coaching priorities, or follow-up notes…"
                  />
                </label>

                <div className="teacher-evaluations__form-footer">
                  <p>Saved evaluations become part of the permanent admin history.</p>
                  <button type="submit" disabled={!complete || saving}>
                    {saving ? "Saving…" : "Save evaluation"}
                  </button>
                </div>
              </form>

              <section className="teacher-evaluations__history">
                <div className="teacher-evaluations__section-heading">
                  <div>
                    <span>PRIVATE HISTORY</span>
                    <h3><MdHistory /> Previous evaluations</h3>
                  </div>
                </div>
                {teacherHistory.length ? (
                  <div className="teacher-evaluations__history-list">
                    {teacherHistory.map((evaluation, index) => {
                      const average = evaluationAverage(evaluation);
                      return (
                        <details key={evaluation.id} open={index === 0}>
                          <summary>
                            <span>
                              <strong>{readableDate(evaluation.createdAt)}</strong>
                              <small>
                                Recorded by {evaluation.evaluatorName || "an administrator"}
                              </small>
                            </span>
                            <span className="teacher-evaluations__history-average">
                              <MdStar /> {average?.toFixed(1) || "—"}
                            </span>
                          </summary>
                          <div className="teacher-evaluations__history-body">
                            <div className="teacher-evaluations__history-metrics">
                              {METRICS.map((metric) => (
                                <div key={metric.key}>
                                  <span>{metric.label}</span>
                                  <strong>{evaluation.metrics?.[metric.key]?.score}/5</strong>
                                  <p>{evaluation.metrics?.[metric.key]?.note || "No note added."}</p>
                                </div>
                              ))}
                            </div>
                            {evaluation.overallSummary && (
                              <div className="teacher-evaluations__history-summary">
                                <strong>Overall summary</strong>
                                <p>{evaluation.overallSummary}</p>
                              </div>
                            )}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                ) : (
                  <div className="teacher-evaluations__history-empty">
                    <MdHistory />
                    <p>The first saved evaluation will appear here.</p>
                  </div>
                )}
              </section>
            </>
          ) : (
            <div className="teacher-evaluations__empty is-large">
              {loading ? "Loading teacher evaluations…" : "Select an active teacher to begin."}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default TeacherEvaluations;
